import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullWidthLayout from '../layouts/FullWidthLayout';
import Container from '../components/Container';
import Box from '../components/Box';
import * as Api from '../services/api';
import { useAppContext } from '../context/AppContext';
import ShoppingCart from '../components/ShoppingCart';
import TagSearchResults from '../components/TagSearchResults';
import './MarketplacePage.css';
import AppButtons from '../components/Common/AppButtons';
import { ROUTES } from '../config/routes';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Grid from '@mui/joy/Grid';
import { formatAndRound, formatter } from '../utils/currencyFormatter';
import { List, ListItem, Modal, ModalClose, ModalDialog } from '@mui/joy';
import moment from 'moment';
import { produce } from 'immer';
import { createTracker } from '../utils/log';

type Tag = {
  tag: string;
  ownerId: string;
  ownerName?: string;
  ownerEmail: string;
  ownerPhone?: string;
  lastUpdatedTime: string;
  lastUpdatedBy: string;
  isNotForSale: boolean;
  verificationRequired: boolean;
  verificationRequiredReason: string;
  holdExpiresTime: string;
  holdFor: string;
  instantSellAtFMV: boolean;
  instantSellPrice: number;
  instantSellSetBy: string;
  instantSellSetTime: string;
  instantSellJustification: string;
  clout: any;
  cloutUpdateTime: string;
  currentPrice: number;
};

// TODO: Deprecate this type
type LocalTag = {
  text: string;
  price: string;
  isPopular: boolean | undefined | null; // has been checked and is sure, not sure because endpoint crashed, has not been checked
  isNotForSale: boolean;
};

type CompleteTag = {
  tag: string;
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerHistory: any;
  lastUpdatedTime: string;
  lastUpdatedBy: string;
  nftMintedTime: string;
  xrplTokenId: string;
  xrplAccount: string;
  xrplSyncTime: string;
  isNotForSale: boolean;
  verificationRequired: boolean;
  verificationRequiredReason: string;
  verificationCompletedTime: string;
  verificationMethod: string;
  verifiedBy: string;
  websiteDomain: string;
  isWebsiteConfirmed: boolean;
  holdExpiresTime: string;
  holdFor: string;
  instantSellAtFMV: boolean;
  instantSellPrice: number;
  instantSellSetBy: string;
  instantSellSetTime: string;
  instantSellJustification: string;
  clout: any;
  cloutUpdateTime: string;
  createdTime: string;
};

const MarketplacePage = (props) => {
  const { cart, setCart, andee, canCheckout, sessionInfo } = useAppContext();
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [recentlyClaimedTags, setRecentlyClaimedTags] = useState<Tag[]>([]);
  const [searchResults, setSearchResults] = useState<LocalTag[]>([]);
  const [addToCartQueue, setAddToCartQueue] = useState<LocalTag[]>([]);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const trackEvent = createTracker(andee, sessionInfo);

  useEffect(() => {
    trackEvent('page_view', { page_name: 'marketplace' });
    trackEvent('logged_in', 'true');
  }, []);

  useEffect(() => {
    if (!andee) {
      trackEvent('logged_in', 'false');
      return;
    }
    setIsSearching(true);
    Api.generateRecommendations(andee.firstName, andee.lastName)
      .then((recs) => updatePrices(recs as LocalTag[]))
      .then((updatedRecs) => {
        setSearchResults(updatedRecs);
      })
      .catch((e) => {
        console.error(e);
        setIsSearching(false);
      })
      .finally(() => setIsSearching(false));
  }, [andee]);

  async function updatePrices(tags: LocalTag[]) {
    const updatedTags: LocalTag[] = [];

    for (const tag of tags) {
      // If the tag already has a price, format and skip the prediction
      if (tag.price) {
        updatedTags.push({
          ...tag,
          price: formatter.format(parseInt(tag.price)),
        });
        continue;
      }
      await Api.getTagPrice(tag.text)
        .then(({ prediction }) => {
          // TODO: getting negative numbers
          const intValue = Math.abs(parseInt(prediction));
          const updatedTag: LocalTag = {
            ...tag,
            price: formatter.format(intValue),
          };
          updatedTags.push(updatedTag);
          // return updatedTag;
        })
        .catch((e) => {
          console.error(e);
          return tag;
        });
    }
    return updatedTags;
  }

  useEffect(() => {
    Api.getRecentlyClaimedTags().then((tags) => setRecentlyClaimedTags(tags));
  }, []);

  useEffect(() => {
    async function processAddToCartQueue() {
      if (addToCartQueue.length > 0) {
        const tag = addToCartQueue[0] as LocalTag;
        try {
          const cleanTag = tag.text.replace('&', '');
          const response = await Api.isTagPopular(cleanTag);
          if (!response || (response as any).error) {
            throw new Error('Error checking popularity');
          }
          const isPopular = response?.popularity?.isPopular || false;
          // TODO: deprecate text and use tag
          await updateItemInCartByTag(tag.text, { isPopular });
        } catch (error) {
          console.error(error);
          await updateItemInCartByTag(tag.text, { isPopular: true });
        }

        setAddToCartQueue((prev) => prev.slice(1));
      }
    }

    processAddToCartQueue();
  }, [addToCartQueue, cart]);

  const handleAddToCart = async (tag) => {
    trackEvent('attempt_to_add_to_cart', tag.text);
    // If there's one in cart.tags already, don't add it. cart can only have one tag at the moment. TODO: remove this limit for public launch
    if (cart.tags.length > 0) {
      trackEvent('add_to_cart_error', 'limit_reached');
      setShowLimitWarning(true);
      return;
    }
    const newTag = { ...tag, isPopular: null };
    // TODO: Put this back before public launch
    // const { canPurchase } = await Api.canAndeePurchaseTags();
    // if (canPurchase) {
    await Api.addToCart(cart, newTag, andee)
      .then((newCart) => setCart(newCart))
      .then(() => setAddToCartQueue((prev) => [...prev, newTag]))
      .catch(() => {}); // ignore rejected promise
    // } else {
    //   setShowLimitWarning(true);
    // }
    trackEvent('added_to_cart', tag.text);
  };

  const updateItemInCartByTag = (tag, update) => {
    setCart((prevCart) => {
      const itemIndex = prevCart.tags.findIndex((item) => {
        // TODO: deprecate text
        return item.tag === tag || item.text === tag;
      });

      if (itemIndex === -1) {
        // If the item with the specified tag doesn't exist, return the previous cart object
        return prevCart;
      }

      // Use immer to create a copy of the cart object that can be safely modified
      const updatedCart = produce(prevCart, (draftCart) => {
        draftCart.tags[itemIndex] = {
          ...draftCart.tags[itemIndex],
          ...update,
        };
      });

      return updatedCart;
    });
  };

  const handleRemoveFromCart = (tag) => {
    trackEvent('remove_from_cart', tag.text);
    Api.removeFromCart(cart, tag)
      .then((newCart) => setCart(newCart))
      .catch((e) => {
        // TODO: error handling
      });
  };

  const handleOnChange = (event: any) => {
    const regex = /^[a-zA-Z0-9]*$/; // allows only alphanumeric characters

    if (!regex.test(event.nativeEvent.data)) {
      // test each character entered
      trackEvent('search', { key: searchKey, error: 'invalid_character' });
      event.preventDefault();
      return;
    }
    setSearchKey(event.target.value.toLowerCase());
  };

  const handleSearchKeyDown = (event) => {
    // prevent & from being entered twice
    if (searchKey.includes('&') && event.key === '&') {
      trackEvent('search', {
        key: searchKey,
        error: 'andee entered & more than once',
      });
      event.preventDefault();
      return;
    }

    // prepend & if not present
    if (!searchKey.includes('&')) {
      setSearchKey('&' + searchKey);
    }

    if (searchKey.trim().length < 2) {
      trackEvent('search', { key: searchKey, error: 'less than 2 characters' });
      return;
    }

    // enter key press
    if (event.keyCode === 13) {
      trackEvent('search', { key: searchKey, error: 'none' });
      setIsSearching(true);

      Api.searchTag(searchKey)
        .then((data) => Api.handleSearchPromise(data, searchKey))
        .then(async (tag) => {
          const recs = await Api.generateRecommendationsFromSearch(searchKey);
          return [{ ...tag, isDirectMatch: true }, ...recs] as LocalTag[];
        })
        .then((multipleRecs) => updatePrices(multipleRecs))
        .then((updatedRecs) => {
          trackEvent('search_results', {
            key: searchKey,
            results: updatedRecs,
          });
          setSearchResults(updatedRecs);
        })
        .catch((e) => {
          trackEvent('search_error', { key: searchKey, error: e });
          console.error(e);
          setIsSearching(false);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  };

  function claimedTagsElement(tag: Tag) {
    const priceRaw = tag.currentPrice;
    let price: number | string = priceRaw;
    if (typeof priceRaw === 'string') {
      price = parseInt((priceRaw as string).replace('$', '').replace(',', ''));
    }

    return (
      <ListItem key={tag.tag}>
        <strong>&{tag.tag}</strong>
        <span>&nbsp;for {formatAndRound(price)}</span>
        <span>&nbsp;&nbsp;</span>
        <span style={{ color: 'var(--and-palette-text-primary)' }}>
          {moment(tag.lastUpdatedTime).fromNow()}
        </span>
      </ListItem>
    );
  }

  return (
    <FullWidthLayout>
      <Container>
        <Typography level="display1">Marketplace</Typography>
        <br />
        {/* TODO: this translates to <p></p> which isn't a valid nesting of elements. change it.  */}
        <Typography level="body2" sx={{ fontSize: '1.2rem' }}>
          {/* TODO: implement search */}
          <Input
            placeholder="find your &tag"
            value={searchKey}
            size="lg"
            sx={{ fontSize: '1.5rem', mb: 2 }}
            onChange={handleOnChange}
            onKeyDown={handleSearchKeyDown}
            endDecorator={<FontAwesomeIcon icon={faSearch} />}
          />
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            <Grid xs={12} md={7}>
              <Typography level="h2">Premium</Typography>
              {isSearching && <p>Searching...</p>}
              {isSearching === false && (
                <TagSearchResults
                  searchKey={searchKey}
                  results={searchResults}
                  onAddToCart={handleAddToCart}
                />
              )}
            </Grid>
            <Grid xs={12} md={5}>
              <Typography level="h2">Cart</Typography>
              <ShoppingCart
                cart={cart}
                buttonText="Checkout"
                onRemoveFromCart={handleRemoveFromCart}
                onButtonClick={() => {
                  if (canCheckout) {
                    navigate(ROUTES.CART);
                  }
                }}
              />
            </Grid>
          </Grid>
          <Box title="Free" backgroundColor="#FAFAFA" style={{ marginTop: 50 }}>
            <p>
              <b>
                &{andee?.firstName?.toLowerCase()}.
                {andee?.lastName?.toLowerCase()}
              </b>{' '}
              may be available. To claim it, get the app.
            </p>
            <AppButtons />
          </Box>
          {!!recentlyClaimedTags.length && (
            <Box title="Recently Claimed" backgroundColor="#F0F0F0">
              <List>
                {recentlyClaimedTags.map((tag: Tag) => claimedTagsElement(tag))}
              </List>
            </Box>
          )}
        </Typography>
        <Modal open={showLimitWarning}>
          <ModalDialog
            color="neutral"
            layout="center"
            size="md"
            variant="plain"
          >
            <ModalClose onClick={() => setShowLimitWarning(false)} />
            <Typography level="h4" textColor="#000">
              Private Beta Purchase Limit
            </Typography>
            <strong>You've reached the limit of 1 tag per account.</strong>
            <p>
              We're temporarily limiting each person to purchasing 1 premium
              &tag. We'll lift this limit soon, as we open the marketplace to
              the public. You'll be able to purchase as many as you'd like at
              that point.
            </p>
            <p>
              In the meantime, you can use the & app to start connecting with
              people, building your profile and checking out what you can do
              now.
            </p>
            <p>
              We have a lot of exciting new features in the works, and we want
              you to be one of the first to experience them. Any and all
              feedback is welcome. Thanks for your support!
            </p>
          </ModalDialog>
        </Modal>
        ;
      </Container>
    </FullWidthLayout>
  );
};

export default MarketplacePage;
